import { Button } from '@lfg/ui/components';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { isAllowedGreadterThanZero } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';
import { RecipientPercent } from './RecipinentPercent';

interface PhaseItemProps {
  index: number;
  isModifying?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onDelete: (e: React.MouseEvent<HTMLButtonElement | HTMLOrSVGElement>) => void;
}
export function PhaseItem({ index, isModifying, onDelete, onEdit, onSave }: PhaseItemProps) {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext<ProjectInputs>();
  const currentPhase = watch(`phases.${index}`);
  const recipients = watch('recipients') || [];
  const errorMessage = errors?.phases?.[index]?.totalNode?.message;
  const { hasDeployed } = useDeployment();
  const recipientMap = useMemo(
    () =>
      recipients?.reduce((acc: Record<string, string>, recipient) => {
        acc[recipient.address] = recipient.name;
        return acc;
      }, {}),
    [recipients],
  );

  useEffect(() => {
    if (!currentPhase?.recipients || isModifying || hasDeployed) return;

    const recipientsWithMissingNames = currentPhase.recipients.filter(
      recipient => recipient.address && !recipientMap[recipient.address],
    );

    if (recipientsWithMissingNames.length > 0) {
      // Clear recipient addresses that don't have names
      const updatedRecipients = currentPhase.recipients.map(recipient => ({
        ...recipient,
        address: recipient.address && recipientMap[recipient.address] ? recipient.address : '',
      }));

      // Update the phase with cleared addresses
      setValue(`phases.${index}.recipients`, updatedRecipients);
      onEdit();
    }
  }, [currentPhase?.recipients, recipientMap, onEdit, isModifying, setValue, index, hasDeployed]);

  return (
    <>
      <Box width={'100%'} padding={'16px 24px'} border={`1px solid ${colors.neutral015}`}>
        {isModifying ? (
          <Box>
            <Typography fontWeight={500} fontSize={'16px'} color='white' paddingBottom={2}>
              Phase {index + 1}
            </Typography>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Total Core Sold
            </Typography>
            <Controller
              name={`phases.${index}.totalNode`}
              control={control}
              render={({ field: { onChange, onBlur, ...rest } }) => (
                <AdminNumberFormat
                  suffix=''
                  prefix=''
                  disabled={hasDeployed}
                  isAllowed={isAllowedGreadterThanZero}
                  onValueChange={values => {
                    onChange(Number(values.floatValue) || 0);
                  }}
                  isError={!!errors?.phases?.[index]?.totalNode}
                  errorMsg={errorMessage}
                  placeholder={'E.g., 100'}
                  {...rest}
                />
              )}
            />
            <RecipientPercent currentPhaseIndex={index} />
            <Stack flexDirection={'row'} justifyContent={'end'} paddingTop={3} gap={1} zIndex={10}>
              <Button
                bgColor={colors.neutral06}
                onClick={onDelete}
                sx={{
                  padding: '10px 32px !important',
                  fontWeight: '500 !important',
                  lineHeight: '20px !important',
                  borderRadius: '4px !important',
                  height: '48px !important',
                }}>
                Cancel
              </Button>
              <Button
                bgColor={`${colors.blue200}`}
                onClick={onSave}
                sx={{
                  padding: '10px 32px !important',
                  fontWeight: '500 !important',
                  lineHeight: '20px !important',
                  borderRadius: '4px !important',
                  height: '48px !important',
                }}>
                Save
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography fontWeight={500} fontSize={'14px'} color='white' paddingBottom={2}>
              Phase {index + 1}:{' '}
              <span
                style={{
                  fontWeight: '400',
                }}>
                {currentPhase?.totalNode}
              </span>
            </Typography>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Stack gap={1}>
                {currentPhase.recipients.map(recipient => (
                  <Stack key={recipient.address} direction={'row'} columnGap={10}>
                    <Typography width={'200px'} color={colors.neutral90} fontSize={'14px'} variant='h6'>
                      {recipientMap[recipient.address]}
                    </Typography>
                    <Typography color='white' fontSize={'14px'} variant='h6'>
                      {recipient.percent}%
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Show when={!hasDeployed}>
                <Stack direction={'row'} gap={1}>
                  <BorderColorIcon
                    onClick={onEdit}
                    sx={{
                      cursor: 'pointer',
                      color: colors.neutral200,
                    }}
                  />
                  <DeleteIcon
                    className='cursor-pointer'
                    onClick={onDelete}
                    sx={{
                      cursor: 'pointer',
                      color: colors.error,
                    }}
                  />
                </Stack>
              </Show>
            </Stack>
          </Box>
        )}
      </Box>
      {errorMessage && !isModifying ? (
        <Typography color={colors.error} fontSize={'14px'}>
          {errorMessage}
        </Typography>
      ) : null}
    </>
  );
}
